import { FC, FormEvent, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { OrganizationRole, UpdateOrganizationSettingsBody } from "~/api/model";
import {
  useGetOrganization,
  useGetOrganizationSettings,
  useUpdateOrganizationSettings,
} from "~/api/organizations/organizations";
import logoImage from "~/assets/logo.svg";
import { LocalizationContext } from "~/components/LocalizationWrapper";
import { SaveButton, SaveButtonRef } from "~/components/SaveButton";
import {
  type FontFamilyOptionKey,
  isFontFamilyOptionKey,
} from "~/features/settings/constants";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import logger from "~/utils/logger";
import { SettingsBranding } from "./SettingsBranding";
import { SettingsLang } from "./SettingsLang";
import { SettingsPreview } from "./SettingsPreview";
import { SettingsTypography } from "./SettingsTypography";

const getFontFamilyOrDefault = (
  value: string | null | undefined
): FontFamilyOptionKey => {
  return isFontFamilyOptionKey(value) ? value : "serif";
};

export const SettingsAppearance: FC = () => {
  useTrackPageVisit("app/settings_appearance/visit");
  const { mutateAsync: updateSettingsMutation, isPending } =
    useUpdateOrganizationSettings();
  const { organizationId } = useOrganizationParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);
  const { data: organization } = useGetOrganization(organizationId);
  const { locale, setLocale } = useContext(LocalizationContext);
  const saveButtonRef = useRef<SaveButtonRef>(null);

  const [pendingLocale, setPendingLocale] = useState(locale);
  const [fontFamily, setFontFamily] = useState<FontFamilyOptionKey>(
    getFontFamilyOrDefault(settings?.fontFamily)
  );
  const [lineSpacing, setLineSpacing] = useState(
    settings?.lineSpacing ?? "1.5"
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(
    settings?.logoUrl ?? null
  );
  const [footnote, setFootnote] = useState<string>(
    (settings as any)?.footnote ?? ""
  );

  const updateSettings = async (data: UpdateOrganizationSettingsBody) => {
    try {
      await updateSettingsMutation({
        organizationId,
        data,
      });
      if (data.fontFamily !== undefined) {
        settings!.fontFamily = data.fontFamily ?? "serif";
      }
      if (data.lineSpacing !== undefined) {
        settings!.lineSpacing = data.lineSpacing ?? "1.5";
      }
      if (data.logoS3Key === null) {
        settings!.logoUrl = null;
      }
      if (data.footnote !== undefined) {
        (settings as any).footnote = data.footnote;
      }
    } catch (error) {
      logger.error("Failed to update organization settings:", error);
      throw error;
    }
  };

  const draftsInitializedRef = useRef(false);

  useEffect(() => {
    setLogoUrl(settings?.logoUrl ?? null);
  }, [settings?.logoUrl]);

  useEffect(() => {
    if (!settings || draftsInitializedRef.current) {
      return;
    }
    draftsInitializedRef.current = true;
    setFontFamily(getFontFamilyOrDefault(settings.fontFamily));
    setLineSpacing(settings.lineSpacing ?? "1.5");
    setFootnote((settings as any).footnote ?? "");
  }, [settings]);

  useEffect(() => {
    setPendingLocale(locale);
  }, [locale]);

  const previewLogo = logoUrl ?? logoImage;
  const canManageAppearance =
    organization?.role === OrganizationRole.OWNER ||
    organization?.role === OrganizationRole.SUPERADMIN;

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (canManageAppearance && settings) {
        await updateSettings({
          fontFamily,
          lineSpacing,
          footnote: footnote.trim() || null,
        } as UpdateOrganizationSettingsBody);
      }
      if (pendingLocale !== locale) {
        setLocale(pendingLocale);
      }
      saveButtonRef.current?.onSuccess();
    } catch (error) {
      logger.error("Failed to save appearance settings:", error);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-14 w-full">
      <form
        onSubmit={handleSave}
        className="flex flex-col gap-6 mt-2 w-full lg:w-[60%]"
      >
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage
              id="settings.appearance"
              defaultMessage={"Appearance"}
            />
          </h3>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.typography"
              defaultMessage={
                "Customize the appearance of your exported reports and documents. These settings will be applied to all PDF and DOCX exports"
              }
            />
          </p>
        </div>
        <SettingsLang
          locale={pendingLocale}
          onLocaleChange={setPendingLocale}
        />
        {canManageAppearance && (
          <>
            <div className="border-t pt-4">
              <h4 className="text-lg font-semibold mb-4">
                <FormattedMessage
                  id="settings.typographyTitle"
                  defaultMessage={"Typography"}
                />
              </h4>

              {settings && organization && (
                <SettingsTypography
                  fontFamily={fontFamily}
                  lineSpacing={lineSpacing}
                  onFontFamilyChange={setFontFamily}
                  onLineSpacingChange={setLineSpacing}
                />
              )}
            </div>

            <div className="border-t pt-4">
              <h4 className="text-lg font-semibold mb-4">
                <FormattedMessage
                  id="settings.branding"
                  defaultMessage={"Branding Assets"}
                />
              </h4>
              <SettingsBranding
                updateSettings={updateSettings}
                logoUrl={logoUrl}
                footnote={footnote}
                onFootnoteChange={setFootnote}
              />
            </div>
          </>
        )}

        <SaveButton isPending={isPending} ref={saveButtonRef} />
      </form>

      <div className="flex flex-col gap-4 mt-2 w-full lg:w-[60%] lg:max-w-[400px]">
        <div>
          <h4 className="text-lg font-semibold mb-2">
            <FormattedMessage
              id="settings.preview"
              defaultMessage={"Preview"}
            />
          </h4>
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.preview.description"
              defaultMessage={
                "See how your settings will appear in exported documents"
              }
            />
          </p>
        </div>
        <SettingsPreview
          fontFamily={fontFamily}
          lineSpacing={lineSpacing}
          logo={previewLogo}
          footnote={footnote.trim() || null}
        />
      </div>
    </div>
  );
};
